/**
 * Subscription API Client
 *
 * API methods for subscription and billing management.
 */

import { apiClient } from './client';

// ============================================
// Types
// ============================================

export interface Subscription {
  id: string;
  storeId: string;
  plan: string;
  status: 'trialing' | 'active' | 'past_due' | 'cancelled' | 'expired';
  billingCycle: 'monthly' | 'annual';
  currency: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  includedDevices: number;
  includedLocations: number;
  maxDevices: number;
  maxStaff: number;
  maxLocations: number;
  bcSubscriptionId: string | null;
  bcSubscriptionStatus: string | null;
  cancelledAt: string | null;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Plan {
  id: string;
  name: string;
  description: string;
  popular?: boolean;
  highlighted?: boolean;
  prices: Record<string, { monthly: number; annual: number; annualTotal: number }>;
  includedDevices: number;
  features: string[];
  limits: {
    maxDevices: number;
    maxStaff: number;
    maxLocations: number;
    apiCallsPerMonth: number;
    dataRetentionDays: number;
  };
  trialDays: number;
  ctaText: string;
}

export interface Addon {
  id: string;
  name: string;
  description: string;
  tagline?: string;
  prices: Record<string, { monthly: number; annual: number }>;
  billingModel: 'flat' | 'per_device' | 'per_location';
  availableOn: string[];
  includedOn: string[];
  enablesFeatures: string[];
  minQuantity?: number;
  maxQuantity?: number;
}

export interface SubscriptionAddon {
  id: string;
  subscriptionId: string;
  addonId: string;
  quantity: number;
  bcLineItemId: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  addonDetails: Addon;
}

export interface SubscriptionWithPlan extends Subscription {
  planDetails: Plan;
  addons: SubscriptionAddon[];
  monthlyTotal: number;
}

export interface SubscriptionEvent {
  id: string;
  subscriptionId: string;
  storeId: string;
  eventType: string;
  eventData: Record<string, unknown> | null;
  idempotencyKey: string | null;
  createdAt: string;
}

export interface SubscriptionLimits {
  devices: {
    included: number;
    additional: number;
    total: number;
    max: number;
  };
  locations: {
    included: number;
    additional: number;
    total: number;
    max: number;
  };
}

export interface GetSubscriptionResponse {
  subscription: SubscriptionWithPlan | null;
  plans: Plan[];
  addons: Addon[];
}

export interface SubscriptionChangeResponse {
  subscription: Subscription;
  message: string;
  mocked: boolean;
}

export interface AddAddonResponse {
  addon: SubscriptionAddon;
  message: string;
  mocked: boolean;
}

// ============================================
// Preview Types (for showing prorated charges)
// ============================================

export interface BillingLineItem {
  type: 'plan' | 'addon';
  name: string;
  unitPrice: number;
  quantity: number;
  total: number;
}

export interface PlanChangePreview {
  changeType: 'upgrade' | 'downgrade';
  currentPlan: { id: string; name: string; price: number };
  newPlan: { id: string; name: string; price: number };
  billing: {
    immediateCharge: number;
    immediateCredit: number;
    newMonthlyTotal: number;
    effectiveDate: string;
    daysRemaining: number;
    daysInPeriod: number;
  };
  lineItems: BillingLineItem[];
  currency: string;
  billingCycle: 'monthly' | 'annual';
}

export interface AddonChangePreview {
  changeType: 'add' | 'increase' | 'decrease' | 'remove';
  addon: {
    id: string;
    name: string;
    unitPrice: number;
    billingModel: string;
  };
  quantity: {
    current: number;
    new: number;
    change: number;
  };
  billing: {
    immediateCharge: number;
    immediateCredit: number;
    currentMonthlyTotal: number;
    newMonthlyTotal: number;
    monthlyChange: number;
    effectiveDate: string;
    daysRemaining: number;
    daysInPeriod: number;
  };
  lineItems: BillingLineItem[];
  currency: string;
  billingCycle: 'monthly' | 'annual';
}

export interface BillingCycleChangePreview {
  changeType: 'switch_to_annual' | 'switch_to_monthly';
  currentCycle: 'monthly' | 'annual';
  newCycle: 'monthly' | 'annual';
  billing: {
    currentMonthlyRate: number;
    newMonthlyRate: number;
    monthlySavings: number;
    annualSavings: number;
    savingsPercent: number;
    annualTotal: number | null;
    effectiveDate: string;
  };
  lineItems: BillingLineItem[];
  currency: string;
}

// ============================================
// API Methods
// ============================================

/**
 * Get current subscription with plan details and addons
 */
async function get(): Promise<GetSubscriptionResponse> {
  return apiClient.request('/subscriptions');
}

/**
 * Get subscription events/history
 */
async function getEvents(): Promise<{ events: SubscriptionEvent[] }> {
  return apiClient.request('/subscriptions/events');
}

/**
 * Get current device and location limits
 */
async function getLimits(): Promise<SubscriptionLimits> {
  return apiClient.request('/subscriptions/limits');
}

/**
 * Preview the billing impact of changing plans
 * Shows prorated charges/credits before confirming
 */
async function previewPlanChange(planId: string): Promise<{ preview: PlanChangePreview }> {
  return apiClient.request('/subscriptions/preview/plan', {
    method: 'POST',
    body: { planId },
  });
}

/**
 * Preview the billing impact of adding/changing an addon
 * Shows prorated charges/credits before confirming
 */
async function previewAddonChange(
  addonId: string,
  quantity: number
): Promise<{ preview: AddonChangePreview }> {
  return apiClient.request('/subscriptions/preview/addon', {
    method: 'POST',
    body: { addonId, quantity },
  });
}

/**
 * Preview the billing impact of changing billing cycle
 * Shows savings when switching to annual
 */
async function previewBillingCycleChange(
  billingCycle: 'monthly' | 'annual'
): Promise<{ preview: BillingCycleChangePreview }> {
  return apiClient.request('/subscriptions/preview/billing-cycle', {
    method: 'POST',
    body: { billingCycle },
  });
}

/**
 * Change subscription plan (upgrade/downgrade)
 * NOTE: Currently mocked until BigCommerce billing is integrated
 */
async function changePlan(planId: string): Promise<SubscriptionChangeResponse> {
  return apiClient.request('/subscriptions/plan', {
    method: 'PUT',
    body: { planId },
  });
}

/**
 * Change billing cycle (monthly <-> annual)
 * NOTE: Currently mocked until BigCommerce billing is integrated
 */
async function changeBillingCycle(billingCycle: 'monthly' | 'annual'): Promise<SubscriptionChangeResponse> {
  return apiClient.request('/subscriptions/billing-cycle', {
    method: 'PUT',
    body: { billingCycle },
  });
}

/**
 * Add or update an addon
 * NOTE: Currently mocked until BigCommerce billing is integrated
 */
async function addAddon(addonId: string, quantity: number): Promise<AddAddonResponse> {
  return apiClient.request('/subscriptions/addons', {
    method: 'POST',
    body: { addonId, quantity },
  });
}

/**
 * Remove an addon
 * NOTE: Currently mocked until BigCommerce billing is integrated
 */
async function removeAddon(addonId: string): Promise<{ removed: boolean; message: string; mocked: boolean }> {
  return apiClient.request(`/subscriptions/addons/${encodeURIComponent(addonId)}`, {
    method: 'DELETE',
  });
}

/**
 * Cancel subscription (at end of billing period)
 * NOTE: Currently mocked until BigCommerce billing is integrated
 */
async function cancel(): Promise<SubscriptionChangeResponse> {
  return apiClient.request('/subscriptions/cancel', {
    method: 'POST',
  });
}

/**
 * Reactivate a cancelled subscription
 * NOTE: Currently mocked until BigCommerce billing is integrated
 */
async function reactivate(): Promise<SubscriptionChangeResponse> {
  return apiClient.request('/subscriptions/reactivate', {
    method: 'POST',
  });
}

export const subscriptionsApi = {
  get,
  getEvents,
  getLimits,
  previewPlanChange,
  previewAddonChange,
  previewBillingCycleChange,
  changePlan,
  changeBillingCycle,
  addAddon,
  removeAddon,
  cancel,
  reactivate,
};
